import React from 'react';
import { Sparkles, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import ContentAccordion from '../components/common/ContentAccordion';
import { retreatContent } from '../data/retreatContent';

const DiscernmentPage = () => {
  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--secondary)] to-[var(--secondary)] flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-heading text-2xl text-[var(--primary)] font-bold mb-1">
          {retreatContent.discernmentPillars.title}
        </h1>
        <p className="text-gray-500 text-sm italic">{retreatContent.discernmentPillars.subtitle}</p>
      </div>

      {/* Intro */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
        <p className="text-gray-700 leading-relaxed text-sm">
          {retreatContent.discernmentPillars.intro}
        </p>
      </div>

      {/* Consolation vs Desolation Quick Guide */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
          <h4 className="font-semibold text-green-800 text-sm mb-2">Consolation</h4>
          <ul className="text-xs text-green-700 space-y-1">
            <li>• Peace and joy</li>
            <li>• Closer to God</li>
            <li>• Hope and trust</li>
            <li>• Clarity</li>
          </ul>
        </div>
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <XCircle className="w-6 h-6 text-red-600 mb-2" />
          <h4 className="font-semibold text-red-800 text-sm mb-2">Desolation</h4>
          <ul className="text-xs text-red-700 space-y-1">
            <li>• Anxiety and unrest</li>
            <li>• Distant from God</li>
            <li>• Doubt and fear</li>
            <li>• Confusion</li>
          </ul>
        </div>
      </div>

      {/* The Pillars */}
      <div>
        <h3 className="font-heading font-semibold text-[var(--primary)] mb-4">The Pillars of Discernment</h3>
        <div className="space-y-3">
          {retreatContent.discernmentPillars.pillars.map((pillar) => (
            <ContentAccordion
              key={pillar.number}
              title={`${pillar.number}. ${pillar.title}`}
              summary={pillar.description.substring(0, 80) + '...'}
            >
              <div className="space-y-3">
                <p className="text-gray-700 text-sm leading-relaxed">
                  {pillar.description}
                </p>
                
                {pillar.rule && (
                  <div className="bg-[var(--primary)]/5 rounded-lg p-3 border-l-4 border-[var(--secondary)]">
                    <p className="text-sm text-[var(--primary)]">
                      <strong>Rule:</strong> {pillar.rule}
                    </p>
                  </div>
                )}

                {pillar.times && (
                  <div className="space-y-2">
                    {pillar.times.map((time, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <p className="font-medium text-gray-800 text-sm">{time.name}</p>
                        <p className="text-xs text-gray-600 mt-1">{time.description}</p>
                      </div>
                    ))}
                  </div>
                )}

                {pillar.warning && (
                  <div className="bg-amber-50 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">{pillar.warning}</p>
                  </div>
                )}
              </div>
            </ContentAccordion>
          ))}
        </div>
      </div>

      {/* Practical Steps */}
      <ContentAccordion title="Practical Steps for Discernment" summary="How to apply these principles">
        <ol className="space-y-3 text-sm text-gray-600">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--primary)] text-white text-xs flex items-center justify-center flex-shrink-0">1</span>
            <div>
              <strong className="text-gray-800">Pray for guidance</strong>
              <p>Begin any discernment process by asking the Holy Spirit for wisdom and clarity.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--primary)] text-white text-xs flex items-center justify-center flex-shrink-0">2</span>
            <div>
              <strong className="text-gray-800">Notice your movements</strong>
              <p>Pay attention to feelings of consolation and desolation as you consider options.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--primary)] text-white text-xs flex items-center justify-center flex-shrink-0">3</span>
            <div>
              <strong className="text-gray-800">Seek wise counsel</strong>
              <p>Talk with a spiritual director or trusted mentor about what you're experiencing.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--primary)] text-white text-xs flex items-center justify-center flex-shrink-0">4</span>
            <div>
              <strong className="text-gray-800">Don't rush</strong>
              <p>Important decisions deserve time. Avoid making major changes during desolation.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-[var(--primary)] text-white text-xs flex items-center justify-center flex-shrink-0">5</span>
            <div>
              <strong className="text-gray-800">Trust the process</strong>
              <p>God is faithful and will guide you. Trust that clarity will come in His timing.</p>
            </div>
          </li>
        </ol>
      </ContentAccordion>

      {/* Quote */}
      <div className="bg-[var(--primary)] rounded-xl p-4 text-white text-center">
        <p className="italic text-sm leading-relaxed">
          "In all things, love and serve the Lord, and do all for His greater glory."
        </p>
        <p className="text-white/60 text-xs mt-2">— St. Ignatius of Loyola</p>
      </div>
    </div>
  );
};

export default DiscernmentPage;
